import { AppAssessments } from "@/interfaces/data_objects/app_assessments";

/**
 * Centralized Assessment Score Calculation Utilities
 *
 * This is the SINGLE SOURCE OF TRUTH for all assessment score calculations.
 * All components should use these functions to ensure consistency across the application.
 */

/**
 * Calculate score from AI metrics (coherence, lexical_resource, grammar)
 * Uses Calculation 1: (coherence + lexical + grammar) / 3
 * Converts from 0-5 scale to 0-100 percentage
 */
export function calculateScoreFromAIMetrics(
  coherence: number,
  lexical_resource: number,
  grammar: number
): number {
  const avgScore = (coherence + lexical_resource + grammar) / 3;
  return Math.min((avgScore / 5) * 100, 100);
}

/**
 * Calculate score for a specific question filter type
 * @param details - Assessment result details array
 * @param filter - The question filter type (reading_questions, listening_questions, speaking, written, mcqs)
 * @returns Score as percentage (0-100) or null if no questions found
 */
export function calculateScoreForFilter(
  details: AppAssessments.AssessmentResultDetails[],
  filter: AppAssessments.QuestionFilter
): number | null {
  const questions = details.filter((item) => {
    const questionFilter = item.question?.question_options?.filter as
      | string
      | undefined;
    // Handle speaking filters (speaking, speaking_task_a, speaking_task_b)
    if (filter === AppAssessments.QuestionFilter.SPEAKING) {
      return (
        questionFilter === "speaking" ||
        questionFilter === "speaking_task_a" ||
        questionFilter === "speaking_task_b" ||
        questionFilter?.startsWith("speaking")
      );
    }
    return questionFilter === filter;
  });

  if (questions.length === 0) return null;

  let totalScore = 0;
  let scoredQuestions = 0;

  for (const question of questions) {
    const assessmentResult = question.assessment_result;

    if (assessmentResult && typeof assessmentResult === "object") {
      const coherence = assessmentResult.coherence || 0;
      const lexical = assessmentResult.lexical_resource || 0;
      const grammar = assessmentResult.grammar || 0;

      totalScore += calculateScoreFromAIMetrics(coherence, lexical, grammar);
      scoredQuestions++;
    }
  }

  if (scoredQuestions === 0) return null;

  const average = totalScore / scoredQuestions;
  return Math.min(average, 100);
}

/**
 * Calculate MCQ score from assessment details
 * @param details - Assessment result details array
 * @returns Score as percentage (0-100) or null if no MCQ questions found
 */
export function calculateMCQScore(
  details: AppAssessments.AssessmentResultDetails[]
): number | null {
  const mcqQuestions = details.filter(
    (item) =>
      item.question?.question_options?.filter ===
      AppAssessments.QuestionFilter.MCQS
  );

  if (mcqQuestions.length === 0) return null;

  const correctAnswers = mcqQuestions.filter(
    (item) => item.assessment_result === 1
  ).length;

  return (correctAnswers / mcqQuestions.length) * 100;
}

/**
 * Calculate writing score from english_writing_score field
 * Writing score is stored as a value out of ~200, so divide by 2 to get percentage
 * @param english_writing_score - Raw writing score from database
 * @returns Score as percentage (0-100) or null if not available
 */
export function calculateWritingScore(
  english_writing_score?: number
): number | null {
  if (!english_writing_score) return null;
  return Math.min(Math.round(english_writing_score / 2), 100);
}

/**
 * Calculate reading score for display column
 * Uses average of AI metrics multiplied by 20 to scale to percentage
 * @param details - Assessment result details array
 * @returns Score as percentage (0-100) or null if no reading questions
 */
export function calculateReadingDisplayScore(
  details: AppAssessments.AssessmentResultDetails[]
): number | null {
  let readingTotal = 0;
  let readingCount = 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  details.forEach((detail: any) => {
    if (!detail.question || !detail.assessment_result) return;

    const filter =
      detail.question.filter || detail.question.question_options?.filter;
    const result = detail.assessment_result;

    if (filter === "reading_questions") {
      const coherence = result.coherence || 0;
      const lexical = result.lexical_resource || 0;
      const grammar = result.grammar || 0;
      const avgScore = (coherence + lexical + grammar) / 3;
      readingTotal += avgScore;
      readingCount++;
    }
  });

  if (readingCount === 0) return null;
  return Math.round((readingTotal / readingCount) * 20);
}

/**
 * Calculate listening score for display column
 * Uses average of AI metrics multiplied by 20 to scale to percentage
 * @param details - Assessment result details array
 * @returns Score as percentage (0-100) or null if no listening questions
 */
export function calculateListeningDisplayScore(
  details: AppAssessments.AssessmentResultDetails[]
): number | null {
  let listeningTotal = 0;
  let listeningCount = 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  details.forEach((detail: any) => {
    if (!detail.question || !detail.assessment_result) return;

    const filter =
      detail.question.filter || detail.question.question_options?.filter;
    const result = detail.assessment_result;

    if (filter === "listening_questions") {
      const coherence = result.coherence || 0;
      const lexical = result.lexical_resource || 0;
      const grammar = result.grammar || 0;
      const avgScore = (coherence + lexical + grammar) / 3;
      listeningTotal += avgScore;
      listeningCount++;
    }
  });

  if (listeningCount === 0) return null;
  return Math.round((listeningTotal / listeningCount) * 20);
}

/**
 * Calculate speaking score for display column
 * Uses average of AI metrics multiplied by 20 to scale to percentage
 * @param details - Assessment result details array
 * @returns Score as percentage (0-100) or null if no speaking questions
 */
export function calculateSpeakingDisplayScore(
  details: AppAssessments.AssessmentResultDetails[]
): number | null {
  let speakingTotal = 0;
  let speakingCount = 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  details.forEach((detail: any) => {
    if (!detail.question || !detail.assessment_result) return;

    const filter =
      detail.question.filter || detail.question.question_options?.filter;
    const result = detail.assessment_result;

    if (
      filter &&
      (filter.startsWith("speaking") ||
        filter === "speaking_task_a" ||
        filter === "speaking_task_b")
    ) {
      const coherence = result.coherence || 0;
      const lexical = result.lexical_resource || 0;
      const grammar = result.grammar || 0;
      const avgScore = (coherence + lexical + grammar) / 3;
      speakingTotal += avgScore;
      speakingCount++;
    }
  });

  if (speakingCount === 0) return null;
  return Math.round((speakingTotal / speakingCount) * 20);
}

/**
 * Calculate overall English assessment score
 * This is the OFFICIAL method to calculate average score across all components
 * Uses the display column scores to ensure consistency
 * @param english_assessment - The English assessment result object
 * @returns Overall score as percentage (0-100)
 */
export function calculateEnglishAssessmentScore(
  english_assessment: AppAssessments.AssessmentResult
): number {
  if (!english_assessment || !english_assessment.details) return 0;

  const scores: number[] = [];
  const details = english_assessment.details;

  // Add MCQ score if exists
  const mcqScore = calculateMCQScore(details);
  if (mcqScore !== null) scores.push(mcqScore);

  // Add Writing score from english_writing_score field
  const writingScore = calculateWritingScore(
    english_assessment.english_writing_score
  );
  if (writingScore !== null) scores.push(writingScore);

  // Add Speaking score using display calculation
  const speakingScore = calculateSpeakingDisplayScore(details);
  if (speakingScore !== null) scores.push(speakingScore);

  // Add Reading score using display calculation
  const readingScore = calculateReadingDisplayScore(details);
  if (readingScore !== null) scores.push(readingScore);

  // Add Listening score using display calculation
  const listeningScore = calculateListeningDisplayScore(details);
  if (listeningScore !== null) scores.push(listeningScore);

  if (scores.length === 0) return 0;

  const average = scores.reduce((sum, score) => sum + score, 0) / scores.length;
  return Math.min(Math.round(average), 100);
}

/**
 * Calculate overall assessment score (includes both English and Annotation assessments)
 * @param english_assessment - Optional English assessment result
 * @param annotation_assessment - Optional Annotation assessment result
 * @returns Overall score as percentage (0-100)
 */
export function calculateOverallAssessmentScore(
  english_assessment?: AppAssessments.AssessmentResult,
  annotation_assessment?: AppAssessments.AnnotationAssessmentResult
): number {
  const scores: number[] = [];

  // Add English score if exists
  if (english_assessment && english_assessment.details) {
    scores.push(calculateEnglishAssessmentScore(english_assessment));
  }

  // Add Annotation score if exists
  if (annotation_assessment) {
    const annotationScore = getAnnotationPercentage(annotation_assessment);
    scores.push(annotationScore);
  }

  if (scores.length === 0) return 0;

  const average = scores.reduce((sum, score) => sum + score, 0) / scores.length;
  return Math.min(Math.round(average), 100);
}

/**
 * Get annotation assessment percentage
 * Sums up all part scores (part1, part2, etc.)
 * @param annotation_assessment - Annotation assessment result
 * @returns Score as percentage (0-100)
 */
function getAnnotationPercentage(
  annotation_assessment: AppAssessments.AnnotationAssessmentResult
): number {
  return Object.values(AppAssessments.AnnotationAssessmentRefs).reduce(
    (acc, key) => {
      const part = annotation_assessment[key];
      if (part) {
        acc += part.score;
      }
      return acc;
    },
    0
  );
}
